/**
 * Endorsement vocabulary shared by candidate profiles and ballot measures
 * (Documentation/ENDORSEMENTS_PRD.md §5.2). The values mirror the backend's
 * endorsementFields.SOURCE_TYPES and the source_type CHECK on both tables.
 */

export const ENDORSEMENT_SOURCE_TYPES = [
  { value: 'endorser_site', label: "Endorser's own site" },
  { value: 'press_release', label: 'Press release' },
  { value: 'news_report', label: 'News report' },
  { value: 'campaign_site', label: 'Campaign site' },
  { value: 'social_media', label: 'Social media' },
  { value: 'questionnaire', label: 'Questionnaire' },
  { value: 'other', label: 'Other' },
] as const;

export type EndorsementSourceType = (typeof ENDORSEMENT_SOURCE_TYPES)[number]['value'];

export const endorsementSourceTypeLabel = (value: string | null | undefined): string | null =>
  ENDORSEMENT_SOURCE_TYPES.find((t) => t.value === value)?.label ?? null;

/** What an endorsement is tied to: a linked record, or only a typed name. */
export type EndorserKind = 'person' | 'organization' | 'charity' | 'business' | 'text';

export const ENDORSER_KIND_LABELS: Record<EndorserKind, string> = {
  person: 'Person',
  organization: 'Capitol Commons org',
  charity: 'Nonprofit',
  business: 'Business',
  text: 'Name only',
};

/**
 * The endorsement fields every surface reads, whichever table they came from.
 * Snake case: the admin and citizen payloads both carry the raw row shape.
 */
export interface EndorsementRow {
  id: string;
  person_id?: string | null;
  organization_id?: string | null;
  endorser_name: string | null;
  endorser_display?: string | null;
  endorser_kind?: EndorserKind | null;
  person_name?: string | null;
  organization_name?: string | null;
  statement?: string | null;
  source_type?: string | null;
  source_name?: string | null;
  source_url: string | null;
  endorsed_on: string | null;
  /** 'org_position' = the organization published it itself (Pro Public Positions tab). */
  origin?: 'org_position' | null;
}

/** The name to show for an endorsement: linked record first, then the typed name. */
export const endorserDisplayName = (row: Pick<EndorsementRow, 'endorser_display' | 'organization_name' | 'person_name' | 'endorser_name'>): string =>
  row.endorser_display || row.organization_name || row.person_name || row.endorser_name || '—';

/** What both endorsement write routes accept (ENDORSEMENTS_PRD §9). */
export interface EndorsementInput {
  side?: string;
  person_id: string | null;
  /** Picker ref (org:/charity:/business:) — the server resolves it to organization_id. */
  organization_ref: string | null;
  endorser_name: string | null;
  statement: string | null;
  source_type: string | null;
  source_name: string | null;
  source_url: string | null;
  endorsed_on: string | null;
}
